package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import com.qrkontaq.core.auth.Session
import com.qrkontaq.core.config.AppConfig
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.w3c.dom.MessageEvent
import org.w3c.dom.WebSocket

/** Live channel to the restaurant's WebSocket; [onEvent] fires with "call"/"order". */
@Composable
fun RealtimeUpdates(
    rid: String,
    onEvent: (String) -> Unit,
) {
    DisposableEffect(rid) {
        var disposed = false
        var socket: WebSocket? = null
        var retryHandle = 0

        fun connect() {
            if (disposed) return
            val token = Session.accessToken.orEmpty()
            val base = AppConfig.apiUrl
            val wsScheme =
                if (base.startsWith("https")) "wss" + base.substring(5) else "ws" + base.substring(4)
            val url = "$wsScheme/v1/admin/restaurants/$rid/ws?token=$token"
            val ws = WebSocket(url)
            socket = ws
            // Anything published while we were offline is lost — refetch on every (re)connect.
            ws.onopen = { onEvent("sync"); Unit }
            ws.onmessage = { e -> onEvent((e as MessageEvent).data.toString()); Unit }
            ws.onclose = {
                socket = null
                // Reconnect with a small delay unless the screen went away.
                if (!disposed) retryHandle = window.setTimeout({ connect() }, 3000)
                Unit
            }
            ws.onerror = { ws.close(); Unit }
        }
        connect()

        // Mobile browsers freeze timers and kill sockets in background tabs; the moment
        // the waiter comes back (or the network returns), reconnect and resync at once.
        val wake: (org.w3c.dom.events.Event) -> Unit = {
            if (!disposed && document.asDynamic().hidden != true) {
                if (socket == null) {
                    if (retryHandle != 0) window.clearTimeout(retryHandle)
                    connect()
                } else {
                    onEvent("sync")
                }
            }
        }
        document.addEventListener("visibilitychange", wake)
        window.addEventListener("focus", wake)
        window.addEventListener("online", wake)

        onDispose {
            disposed = true
            document.removeEventListener("visibilitychange", wake)
            window.removeEventListener("focus", wake)
            window.removeEventListener("online", wake)
            if (retryHandle != 0) window.clearTimeout(retryHandle)
            socket?.close()
        }
    }
}

/** Sound + OS notification for incoming events (works while the tab is alive). */
object StaffAlerts {
    private var armed = false
    private var suppressUntil = 0.0
    private val audio: dynamic = js("({ ctx: null })")

    /** Wire audio-unlock, notification permission and push subscription to the first gesture. */
    fun arm(rid: String) {
        if (armed) return
        armed = true
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            ensureContext()
            requestNotifyPermission()
            kotlinx.coroutines.MainScope().launch { WebPush.enable(rid) }
        }
        window.addEventListener("click", handler)
        window.addEventListener("touchend", handler)
    }

    /** Mute the beep briefly after a local action so staff don't hear their own taps. */
    fun suppress(ms: Int = 2500) {
        suppressUntil = nowMs() + ms
    }

    fun beep() {
        if (nowMs() < suppressUntil) return
        try {
            ensureContext()
            val c = audio.ctx ?: return
            val osc = c.createOscillator()
            val gain = c.createGain()
            osc.type = "sine"
            osc.frequency.value = 880
            osc.connect(gain)
            gain.connect(c.destination)
            val t = c.currentTime as Double
            gain.gain.setValueAtTime(0.0001, t)
            gain.gain.exponentialRampToValueAtTime(0.35, t + 0.01)
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45)
            osc.start(t)
            osc.stop(t + 0.47)
        } catch (_: Throwable) {
        }
    }

    /** Native notification, shown only when the tab isn't focused. */
    fun notify(
        title: String,
        body: String,
    ) {
        try {
            if (document.asDynamic().hidden != true) return
            val ctor = window.asDynamic().Notification
            if (ctor == null || ctor.permission != "granted") return
            val opts: dynamic = js("({})")
            opts.body = body
            window.asDynamic().Reflect.construct(ctor, arrayOf<dynamic>(title, opts))
        } catch (_: Throwable) {
        }
    }

    private fun requestNotifyPermission() {
        try {
            val ctor = window.asDynamic().Notification
            if (ctor != null && ctor.permission == "default") ctor.requestPermission()
        } catch (_: Throwable) {
        }
    }

    private fun ensureContext() {
        try {
            if (audio.ctx == null) {
                audio.ctx = js("new (window.AudioContext || window.webkitAudioContext)()")
            }
            if (audio.ctx.state == "suspended") audio.ctx.resume()
        } catch (_: Throwable) {
        }
    }

    private fun nowMs(): Double = window.asDynamic().Date.now() as Double
}
